import React, { useEffect } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Call, CallEnd } from '@mui/icons-material';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useCallingContext } from './CallingContext';
import { CallState } from '../../utils/webrtc-config';

interface ChatCallingButtonProps {
  conversationId: string;
  userId: string;
  userType: 'agent' | 'customer';
  customerInfo?: {
    customer_id: string;
    name: string;
  };
  onCallStart?: () => void;
  onCallEnd?: () => void;
  onCallError?: (error: string) => void;
}

/**
 * Audio call button — styled to match the existing MuiIconButton size="small"
 * pattern used throughout the conversation header toolbar.
 */
export const ChatCallingButton: React.FC<ChatCallingButtonProps> = ({
  conversationId,
  userId,
  userType,
  customerInfo,
  onCallStart,
  onCallEnd,
  onCallError,
}) => {
  const sharedContext = useCallingContext();
  const ownWebrtc = useWebRTC({ autoConnect: sharedContext === null });
  const { callState, startCall, endCall, error } = sharedContext ?? ownWebrtc;

  useEffect(() => {
    if (!sharedContext) return;
    if (callState === CallState.CONNECTED) onCallStart?.();
    else if (callState === CallState.ENDED || callState === CallState.IDLE) onCallEnd?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState, sharedContext]);

  const isIdle = callState === CallState.IDLE || callState === CallState.ENDED;
  const isPending =
    callState === CallState.INITIATING || callState === CallState.RINGING ||
    callState === CallState.CONNECTING;
  const isConnected = callState === CallState.CONNECTED;

  const handleClick = async () => {
    try {
      if (isIdle || callState === CallState.ERROR) {
        await startCall(undefined, {
          conversation_id: conversationId,
          customer_id: customerInfo?.customer_id || userId,
          call_type: 'audio',
          agent_id: userType === 'agent' ? userId : undefined,
          user_type: userType,
        });
      } else if (isConnected) {
        await endCall();
      }
    } catch (err) {
      onCallError?.(err instanceof Error ? err.message : 'Call failed');
    }
  };

  const tooltipTitle = isConnected
    ? 'End audio call'
    : isPending
    ? 'Connecting…'
    : error
    ? `Retry (${error})`
    : userType === 'agent'
    ? `Call ${customerInfo?.name ?? 'customer'}`
    : 'Call agent';

  return (
    <Tooltip title={tooltipTitle}>
      {/* Span wrapper lets Tooltip render on disabled buttons */}
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={isPending}
          color={isConnected ? 'error' : 'primary'}
          aria-label={tooltipTitle}
        >
          {isPending ? (
            <CircularProgress size={16} color="inherit" />
          ) : isConnected ? (
            <CallEnd fontSize="small" />
          ) : (
            <Call fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};
